import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import styles from './RecordEditorErrorBoundary.module.css';
import { logError, ErrorSeverity, ErrorCategory } from '../../../services/errorService';
import mapRecordsErrorToKey from '../utils/recordsErrorMapper';

/**
 * Error boundary specifically designed for the RecordEditor modal context.
 * Provides graceful error handling with modal-appropriate UI and recovery options.
 */
class RecordEditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('RecordEditor Error:', error);
    console.error('Error Info:', errorInfo);
    
    // Log to analytics for production monitoring
    logError(error, {
      severity: ErrorSeverity.CRITICAL, // Editor errors prevent user from saving data
      category: ErrorCategory.VALIDATION,
      component: 'RecordEditorErrorBoundary',
      action: 'componentDidCatch',
      metadata: {
        componentStack: errorInfo.componentStack,
        errorCount: this.state.errorCount + 1,
        recordId: this.props.recordId || 'new',
        isEditing: true
      }
    });
    
    // Update state with error details
    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Call optional error reporting callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    // Reset error state when modal is reopened
    if (!prevProps.isOpen && this.props.isOpen && this.state.hasError) {
      this.resetError();
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleClose = () => {
    this.resetError();
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  handleRetry = () => {
    this.resetError();
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorCount } = this.state;
      const isDevelopment = process.env.NODE_ENV === 'development';
      const { t } = this.props;
      const errorMessageKey = mapRecordsErrorToKey(error, {
        fallbackKey: 'records.editor.boundary.message'
      });

      return (
        <div className={styles.errorModal}>
          <div className={styles.errorContent}>
            <div className={styles.errorHeader}>
              <h2 className={styles.errorTitle}>
                <span className={styles.errorIcon}>⚠️</span>
                {t('records.editor.boundary.title', 'Something went wrong')}
              </h2>
              <button
                className={styles.closeButton}
                onClick={this.handleClose}
                aria-label={t('records.editor.boundary.close_aria', 'Close error dialog')}
              >
                ✕
              </button>
            </div>
            
            <div className={styles.errorBody}>
              <p className={styles.errorMessage}>
                {t(errorMessageKey, {
                  defaultValue: t(
                    'records.editor.boundary.message',
                    'We encountered an error while loading the record editor. Your data has not been lost.'
                  )
                })}
              </p>
              
              {errorCount > 2 && (
                <p className={styles.warningMessage}>
                  {t(
                    'records.editor.boundary.repeat_warning',
                    'This error has occurred multiple times. Please refresh the page if the problem persists.'
                  )}
                </p>
              )}

              {isDevelopment && (
                <details className={styles.errorDetails}>
                  <summary>
                    {t(
                      'records.editor.boundary.details_summary',
                      'Error details (development only)'
                    )}
                  </summary>
                  <div className={styles.errorStack}>
                    <p>
                      <strong>{t('records.editor.boundary.error_label', 'Error')}:</strong>{' '}
                      {error?.toString()}
                    </p>
                    <pre>{errorInfo?.componentStack}</pre>
                  </div>
                </details>
              )}
            </div>

            <div className={styles.errorActions}>
              <button
                className={styles.retryButton}
                onClick={this.handleRetry}
                disabled={errorCount > 3}
              >
                {t('common.retry', 'Retry')}
              </button>
              <button
                className={styles.cancelButton}
                onClick={this.handleClose}
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

RecordEditorErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onRetry: PropTypes.func,
  onError: PropTypes.func,
  t: PropTypes.func.isRequired
};

RecordEditorErrorBoundary.defaultProps = {
  isOpen: true,
  onClose: undefined,
  onRetry: undefined,
  onError: undefined
};

export default withTranslation()(RecordEditorErrorBoundary);
